import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useParams, useHistory } from "react-router-dom";
import Page from "components/Page";
import Table from "components/table";
import { withTranslation } from "react-i18next";
import Toast from "components/Toast/Alert";
import Empty from "components/Empty";
import Loader from "components/Loader";
import {
  usePolicyHistory,
  useExportPolicyHistory,
} from "repositories/office-policy-management-repository";
import { decodeId, formatLocalDateTime } from "utils";
import moment from "moment";
import toast from "react-hot-toast";
import "../OfficePolicyManagement.scss";
import styles from "../OfficePolicyManagement.module.scss";
import SeeDetailsModal from "./SeeDetailsModal";
import AssignedAndRenotifyModal from "./AssignedAndRenotifyModal";
import { b64toBlob, handleError } from "../../../../utils";
import FileSaver from "file-saver";
import Text from "components/Text";
import { getPolicyById } from "../../../../repositories/office-policy-management-repository";
import { useQuery } from "react-query";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import constants from "../../../../constants";
import { getLocalTimeZoneCode } from "accountOwner/pages/AddEvent/addEvents.functions";
const PAGE_SIZE = 10;
const actionMap = {
  1: "Created",
  2: "Edited",
  3: "Assigned",
  4: "ReNotified",
};
export const actionValues = {
  Created: 1,
  Edited: 2,
  Assigned: 3,
  ReNotified: 4,
};
const secureFileDownload = (
  base64Content,
  fileName,
  contentType = "application/octet-stream",
  t,
) => {
  try {
    // Validate inputs
    if (!base64Content || !fileName) {
      throw new Error(t("aiAssistant.invalidFile"));
    }

    // Sanitize filename to prevent path traversal attacks
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");

    // Use existing safe utility function
    const blob = b64toBlob(base64Content, contentType);

    // Create a safe download using FileSaver
    FileSaver.saveAs(blob, sanitizedFileName);

    return true;
  } catch (error) {
    handleError(t("aiAssistant.fileDownloadError"));
    return false;
  }
};
function PolicyDetailsHistoryLogs({ t }) {
  const { policyId: encodedPolicyId } = useParams();
  const { selectedId } = useAccountOwnerCommon();
  const history = useHistory();
  const policyId = decodeId(encodedPolicyId);
  const [storePolicyName, setStorePolicyName] = useState(null);
  const { data: policy } = useQuery(
    ["policy-history-details-view", policyId],
    () => getPolicyById(policyId),
    {
      enabled: !!policyId,
    },
  );
  const { data, isLoading, error } = usePolicyHistory(policyId, {
    enabled: !!policyId,
  });
  useEffect(() => {
    if (
      policy?.policyFolder?.ownerId &&
      selectedId &&
      policy?.policyFolder?.ownerId !== selectedId
    ) {
      history.push(
        constants.routes.officePolicyManagements.officePolicyManagement,
      );
    }
  }, [policy?.policyFolder?.ownerId, selectedId]);
  const exportPolicyHistoryMutation = useExportPolicyHistory();

  const goBack = () => history.goBack();
  const [seeDetailsModal, setSeeDetailsModal] = useState(null);
  const [assignedAndRenotifyModal, setAssignedAndRenotifyModal] =
    useState(null);
  const handleSeeDetails = useCallback((row) => {
    if (
      row?.action === actionValues.Assigned ||
      row?.action === actionValues.ReNotified
    ) {
      setAssignedAndRenotifyModal(row);
    } else {
      setSeeDetailsModal(row);
    }
  }, []);
  const handleExport = useCallback(async () => {
    if (!policyId) {
      toast.error("Policy ID is required");
      return;
    }
    const { timeZoneId } = getLocalTimeZoneCode();
    try {
      const blob = await exportPolicyHistoryMutation.mutateAsync({
        policyId,
        timeZoneId,
      });
      if (blob.data.file_Url) {
        const success = secureFileDownload(
          blob.data.file_Url,
          `policy-history-${policyId}-${moment().format("YYYY-MM-DD")}.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          t,
        );
        if (success) {
          toast.success(
            t("officePolicyManagement.exportSuccess") || "Export successful",
          );
        } else {
          toast.error(
            t("officePolicyManagement.exportFailed") || "Export failed",
          );
        }
      } else {
        toast.error(
          t("officePolicyManagement.exportFailed") || "Export failed",
        );
      }
    } catch (err) {
      toast.error(err?.message || "Failed to export policy history");
    }
  }, [policyId, exportPolicyHistoryMutation, t]);

  const getActionClass = useCallback((action) => {
    if (action === actionValues.Created || action === actionValues.Edited)
      return styles["blue"];
    if (action === actionValues.Assigned) return styles["dark-green"];
    if (action === actionValues.ReNotified) return styles["yellow"];
    return styles["blue"];
  }, []);

  const columns = useMemo(
    () => [
      {
        text: "Action",
        dataField: "action",
        attrs: { datatitle: "Action" },
        formatter: (cellContent, row) => {
          if (isLoading) {
            return (
              <div className="text-placeholder-150 shimmer-animation"></div>
            );
          }
          const action = row?.action || 1;
          return (
            <span
              className={`${styles["action-btn"]} ${getActionClass(action)}`}
            >
              {actionMap[action]}
            </span>
          );
        },
      },
      {
        text: "Action Taken On",
        attrs: { datatitle: "Action Taken On" },
        dataField: "policyName",
        formatter: (cellContent, row) => {
          if (isLoading) {
            return (
              <div className="text-placeholder-100 shimmer-animation"></div>
            );
          }
          return (
            <span className={styles["text-color-size"]}>
              {/* {row?.policyName ?? row?.title ?? row?.documentName ?? "—"} */}
              {formatLocalDateTime(row?.createdAt, "ddd, MMM DD, YYYY") || "—"}
            </span>
          );
        },
      },
      {
        text: "Performed by",
        attrs: { datatitle: "Performed by" },
        dataField: "actionTakenOn",
        formatter: (cellContent, row) => {
          if (isLoading) {
            return (
              <div className="text-placeholder-100 shimmer-animation"></div>
            );
          }
          return (
            <span className={styles["text-color-size"]}>
              {row?.createdBy?.name ?? "—"}
            </span>
          );
        },
      },
      {
        text: "Description",
        attrs: { datatitle: "Description" },
        dataField: "performedBy",
        formatter: (cellContent, row) => {
          if (isLoading) {
            return (
              <div className="text-placeholder-100 shimmer-animation"></div>
            );
          }

          if (row?.action !== actionValues.Created) {
            return (
              <button
                className={`${styles["text-color-size"]} ${styles["link-text-button"]}`}
                onClick={() => handleSeeDetails(row)}
              >
                See Details
              </button>
            );
          }
          return <span className={styles["text-color-size"]}>--</span>;
        },
      },
    ],
    [isLoading, t, getActionClass],
  );
  useEffect(() => {
    if (!isLoading && data?.items?.length > 0) {
      setStorePolicyName(data?.items?.[0]?.policyName);
    }
  }, [isLoading, data]);
  const items = data?.items ?? [];
  const itemsLength = items?.length ?? 0;

  let rows = [];
  if (isLoading) {
    rows = new Array(PAGE_SIZE).fill({});
  } else {
    rows = items;
  }

  if (error) {
    return (
      <Page titleKey={t("officePolicyManagement.policyLogs")} onBack={goBack}>
        <Toast
          errorToast
          message={error?.message || "Failed to load history logs"}
        />
      </Page>
    );
  }

  return (
    <Page
      titleKey={t("officePolicyManagement.policyLogs")}
      onBack={goBack}
      actionButton={
        <button
          className={styles["export-btn"]}
          onClick={handleExport}
          disabled={exportPolicyHistoryMutation.isLoading || !policyId}
        >
          {exportPolicyHistoryMutation.isLoading
            ? t("officePolicyManagement.exporting")
            : t("officePolicyManagement.export")}
        </button>
      }
    >
      {storePolicyName && (
        <Text
          className={` ${styles["historylog-sub-text"]} `}
          size="14px"
          weight="300"
          color="#000000"
        >
          {storePolicyName}
        </Text>
      )}
      <div className="hour-rate-table marginTop30">
        {isLoading && <Loader />}
        {!isLoading && itemsLength === 0 && (
          <Empty
            Message={t("officePolicyManagement.noHistoryLogsForSelection")}
          />
        )}
        {!isLoading && itemsLength > 0 && (
          <Table
            columns={columns}
            data={rows}
            keyField="id"
            disablePagination
          />
        )}
      </div>
      {seeDetailsModal && (
        <SeeDetailsModal
          setIsSaveEmployeeModalOpen={() => setSeeDetailsModal(null)}
          issaveEmployeeModalOpen={true}
          data={seeDetailsModal}
        />
      )}
      {assignedAndRenotifyModal && (
        <AssignedAndRenotifyModal
          setIsSaveEmployeeModalOpen={() => setAssignedAndRenotifyModal(null)}
          issaveEmployeeModalOpen={true}
          data={assignedAndRenotifyModal}
        />
      )}
    </Page>
  );
}

export default withTranslation()(PolicyDetailsHistoryLogs);
